import React, { FunctionComponent, memo, useState } from 'react'
import styles from './TenderPicker.module.sass'
import clsx from 'clsx'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import Tooltip from '../../../etc/Tooltip'
import Popover from '../../../etc/Popover'
import TenderIcon from 'files/tender.svg'
import List from '../../lists/List/List'
import { useQueries } from '../../../../utils/hooks/useQueries'
import { Classes, Colors } from '../../../../styles'
import { enumMatchAll } from '../../../../utils/enum-utils'
import { identity } from 'fp-ts/es6/function'

export enum TenderStatus {
    CANCELED = 0,
    IN_WORK = 1,
    WON = 2,
    LOST = 3,
}

export const tenderToColor = enumMatchAll<TenderStatus>()({
    [TenderStatus.CANCELED]: Colors.violet,
    [TenderStatus.IN_WORK]: Colors.blue,
    [TenderStatus.WON]: Colors.green,
    [TenderStatus.LOST]: Colors.red,
})

export const tenderToName = enumMatchAll<TenderStatus>()({
    [TenderStatus.CANCELED]: 'Отменен',
    [TenderStatus.IN_WORK]: 'В работе',
    [TenderStatus.WON]: 'Выигран',
    [TenderStatus.LOST]: 'Проигран',
})

export const tenderToFullName = enumMatchAll<TenderStatus>()({
    [TenderStatus.CANCELED]: 'Не участвует',
    [TenderStatus.IN_WORK]: 'Тендер в работе',
    [TenderStatus.WON]: 'Тендер выигран',
    [TenderStatus.LOST]: 'Тендер проигран',
})

interface TenderProps {
    className?: string,
    state: TenderStatus,
    showDisabled?: boolean,
}

export const Tender: FunctionComponent<TenderProps> = props => <div
    className={clsx(props.className, styles.icon, {
        [styles.invisible]: !props.showDisabled && props.state === TenderStatus.CANCELED
    })}
>
    <TenderIcon style={{stroke: tenderToColor(props.state)}}/>
</div>

interface Props {
    value: TenderStatus,
    full?: boolean,
    disabled?: boolean,
    onChange?: (value: TenderStatus) => void,
}

const TenderPicker: FunctionComponent<Props> = props => {
    const [open, setOpen] = useState(false)
    const {isDesktop} = useQueries()

    if (props.value === TenderStatus.CANCELED) {
        return null
    }

    return <Popover
        isOpen={open && !props.disabled}
        positions={['bottom']}
        align={'center'}
        onClickOutside={() => setOpen(false)}
        content={<ShadowContainer className={styles.popover}>
            <List<TenderStatus>
                data={[TenderStatus.IN_WORK, TenderStatus.LOST, TenderStatus.WON, TenderStatus.CANCELED].filter(status => status !== props.value)}
                onClick={state => {
                    props.onChange?.(state)
                    setOpen(false)
                }}
                render={item => <div className={styles.item}>
                    <Tender showDisabled state={item}/>
                    {
                        isDesktop && <div className={styles.text}>
                            {tenderToName(item)}
                        </div>
                    }
                </div>}
                toKey={identity}
            />
        </ShadowContainer>}
    >
        <div
            className={clsx(styles.tender, Classes.touchTarget, {
                [styles.full]: props.full,
                [styles.interactive]: !props.disabled,
            })}
            onClick={() => !props.disabled && setOpen(!open)}
        >
            <Tooltip
                className={styles.content}
                title={{
                    content: 'Изменить тендер',
                    disable: props.disabled,
                }}
            >
                <Tender state={props.value}/>
                {
                    props.full && <span className={styles.text}>{tenderToFullName(props.value)}</span>
                }
            </Tooltip>
        </div>
    </Popover>
}

export default memo(TenderPicker)